import { HotFilterItem, HotFiltersFacet, HotFiltersFacetsFields } from '@interfaces/api/responses/hot-filters.response';
import { getHotFilterItemById, sortHotFiltersObjectKeysAsc } from './hot-filters';
import { HOT_FILTER_KEY_MAPPINGS, processFilterItems } from './hot-filters-parsing-util';

/**
 * Determines which filters are selected based Filter IDs and names extracted from URL parameters and the data payload.
 *
 * `hotFiltersNestedKeyMappings` maps filter categories to their hierarchical levels:
 * - `category` maps to `['categoryLvl0', 'categoryLvl1']`
 * - `material` maps to `['materialLvl0', 'materialLvl1']`
 */
export const getHotFiltersCheckedStatus = (
  filterIds: Record<string, { id: string; name: string }[]>,
  data: Record<string, HotFilterItem[]>,
): Record<string, boolean> => {
  const checkedStatus: Record<string, boolean> = {};

  Object.keys(filterIds).forEach((key) => {
    // Maps filter categories to their hierarchical levels
    const relevantKeys = HOT_FILTER_KEY_MAPPINGS[key] || [key];
    relevantKeys.forEach((dataKey) => {
      if (data && data[dataKey]) {
        filterIds[key].forEach((filter: { id: string; name: string }) => {
          const id = filter.id.toString();
          const name = filter.name;

          data[dataKey].forEach((item) => {
            if (item.filterId.toString() === id && item.name === name) {
              checkedStatus[`${dataKey}_${item.filterId}`] = true;
            }
          });
        });
      }
    });
  });

  return checkedStatus;
};

// Adds "All items" option to sub-level 1 filters or removes it based on `includeAllItems`.
const handleNestedItems = (result: HotFilterItem[], includeAllItems: boolean = false): void => {
  result.forEach((item) => {
    if (item.isNested) {
      item.children = item.children.map((nestedItem) => {
        // Create a new children array with the "All items" entry added if needed
        const children =
          includeAllItems && nestedItem.children.length > 1
            ? [
                {
                  id: `all_${nestedItem.filterId}`,
                  filterId: `all_${nestedItem.filterId}`,
                  name: 'all_items',
                  parentID: nestedItem.filterId,
                  parentKey: item.filterId,
                  children: [],
                  sibling: nestedItem.children,
                  isChecked: nestedItem.children.every((child) => child.isChecked),
                },
                ...nestedItem.children.filter((child) => !child.filterId.startsWith('all_')),
              ]
            : nestedItem.children.filter((child) => !child.filterId.startsWith('all_'));

        return {
          ...nestedItem,
          children,
        };
      });
    }
  });
};

/**
 * Converts facets fields data into a structured list of hot filter items, 
 * applying sorting, checked status, and handling nested items.
 * 
 * - **Sorting:** Ensures parent items (like categoriesLvl0) come before child items (like categoriesLvl1) for a logical hierarchy.
 * 
 * - **Checked Status:** Marks filter items as selected based on URL parameters (e.g., if the URL contains
 *   `?category=clothing&brand=gucci`, "Clothing" and "Gucci" will be shown as selected).
 * 
 * - **Handling Nested Items:** Adds "All items" option to sub-level 1 filters or removes it based on `includeAllItems`.

 */
export const convertFacetsFieldsToHotFilters = (
  data: Partial<Record<HotFiltersFacetsFields, HotFiltersFacet[]>> | undefined,
  includeAllItems?: boolean,
): HotFilterItem[] => {
  if (!data) {
    return [];
  }

  // Ensures parent items (like categoriesLvl0) come before child items (like categoriesLvl1) for a logical hierarchy.
  const sortedKeys = sortHotFiltersObjectKeysAsc(data);

  const result: HotFilterItem[] = [];
  const map: Record<string, HotFilterItem> = {};

  Object.keys(sortedKeys).forEach((dataKey) => {
    const items = sortedKeys[dataKey].map((item) => ({
      ...item,
      id: item.filterId,
      children: [],
      parentKey: dataKey,
      isChecked: item.isApplied,
    }));

    // Converts filter items into a hierarchical structure, handling parent-child relationships.
    processFilterItems(dataKey, items, map, result);
  });

  // Adds "All items" option to sub-level 1 filters or removes it based on `includeAllItems`.
  handleNestedItems(result, includeAllItems);

  return result;
};

// Updates the checked state of an item and its descendants. Also updates parent items based on their children’s state.
export const applyCheckedStateToHotFilterItem = (items: HotFilterItem[], id: string, isChecked: boolean): void => {
  if (!items) {
    return null;
  }

  // It recursively searches through `items` and their children to find an item with the matching ID.
  const item = getHotFilterItemById(items, id);
  if (item) {
    item.isChecked = isChecked;
    if (item.children) {
      item.children.forEach((child) => applyCheckedStateToHotFilterItem([child], child.id, isChecked));
    }
    let parent = getHotFilterItemById(items, item.parentID);
    while (parent) {
      parent.isChecked = parent.children.some((child) => child.isChecked);
      const allItem = parent.children.find((child) => child.id === `all_${child.parentID}`);
      if (allItem) {
        allItem.isChecked = parent.children.every((child) => child.isChecked || child.id === `all_${child.parentID}`);
      }
      parent = getHotFilterItemById(items, parent.parentID);
    }
  }
};
